
import { AppConst } from '@/constants';
import { Tenant } from '@/store/modules/app/types';
import { Namespace } from '@/store/types';
import { eventBus } from '@/utils/eventBus';
import { debounce } from 'lodash';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Getter, State } from 'vuex-class';

const timeoutMs: number = 10000

/**
 * Analytics component that embeds and manages communication with the JIG analytics dashboard iframe
 * Handles iframe loading, initialization, and bidirectional communication for analytics data
 */
@Component({})
export default class Analytics extends Vue {
  @State('myTenant', { namespace: Namespace.App })
  public myTenant!: Tenant

  @Getter('isPreparingApp', { namespace: Namespace.Utils })
  public isPreparingApp!: boolean

  private idToken: string = this.$auth0.idTokenRaw
  public isInitialising: boolean = false
  private initializingMessage: boolean = false
  private iframeLoaded: boolean = false
  private iframeContentHeight: number = -1
  private setIframeHeight!: (h: number) => void

  public $refs!: Vue['$refs'] & {
    jigStats: HTMLIFrameElement
  }

  @Watch('isPreparingApp')
  private async onIsPreparingAppChanged(value: boolean, oldValue: boolean) {
    if (this.iframeLoaded && oldValue === true && value === false) {
      // Ensure initial DOM is flushed before proceeding
      await this.$nextTick()
      await this.initCommunication()
    }
  }

  protected async mounted() {
    this.isInitialising = true
    this.bindDebounceFunctions()
  }

  protected beforeDestroy() {
    window.removeEventListener('message', this.handleEventEmitFromIframe)
  }

  public get iframeStyle(): Partial<CSSStyleDeclaration> {
    return this.iframeContentHeight < 0 ? {} : { height: `${this.iframeContentHeight}px` }
  }

  public get analyticsUrl(): string {
    return (process.env.VUE_APP_JIG_STATS_URL || '').replace(/\/$/, '')
  }

  bindDebounceFunctions() {
    this.setIframeHeight = debounce((newHeight: number) => {
      const diff = Math.abs(newHeight - this.iframeContentHeight)
      if (diff <= 50) return
      this.iframeContentHeight = newHeight
    }, 120)
  }

  /**
   * Handles the iframe load event for the analytics dashboard
   * Initializes communication once the iframe is loaded and app preparation is complete
   * @param event - The load event from the iframe
   */
  public async onStatsIframeLoaded(event: Event) {
    const iframe = event.target as HTMLIFrameElement

    // Ignore load events from blank iframes (can occur during navigation)
    if (iframe.src === 'about:blank') return

    // Mark iframe as loaded
    this.iframeLoaded = true

    // Ensure initial DOM is flushed before proceeding
    await this.$nextTick()

    // Only initialize communication if app preparation is complete
    if (this.isPreparingApp === false) {
      await this.initCommunication()
    }
  }

  // Navigating away and back fires the iframe's load event while the embedded SPA is still mounting.
  // Ping the child window until it signals readiness so the init payload lands safely.
  private async pingUntilReady() {
    return new Promise<void>((resolve, reject) => {
      const started = Date.now()
      let timer: any
      let interval: any

      const onMsg = (event: MessageEvent) => {
        if (event.origin !== this.analyticsUrl) return
        if (event.source !== this.$refs.jigStats.contentWindow) return
        if (event.data && event.data.type !== AppConst.statsMessageType.statsReady) return
        cleanup()
        resolve()
      }

      const sendPing = () => {
        if (Date.now() - started > timeoutMs) {
          cleanup()
          reject(new Error('Timeout waiting for stats_ready'))
          return
        }
        if (this.$refs.jigStats.contentWindow) {
          this.$refs.jigStats.contentWindow.postMessage({ type: AppConst.statsMessageType.initDashboard, parentOrigin: window.location.origin }, this.analyticsUrl)
        }
      }

      const cleanup = () => {
        clearInterval(interval)
        clearTimeout(timer)
        window.removeEventListener('message', onMsg)
      }

      window.addEventListener('message', onMsg)
      interval = setInterval(sendPing, 200) // retry every 200ms
      timer = setTimeout(() => { /* timeout guard */ }, timeoutMs)
      sendPing() // fire immediately
    })
  }

  /**
   * Initializes communication with the embedded analytics iframe
   * This method ensures the iframe is ready to receive data before sending initialization payload
   */
  private async initCommunication() {
    // Ensure initial DOM is flushed before proceeding
    await this.$nextTick()

    // Prevent multiple simultaneous initialization attempts
    if (this.initializingMessage) return

    // Set flag to prevent concurrent initialization
    this.initializingMessage = true

    // Wait for the iframe to signal it's ready to receive data
    // This handles cases where navigation causes the iframe to reload while the SPA is still mounting
    await this.pingUntilReady()

    // Send the initialization data to the iframe
    this.postInitData()

    // Reset initialization flags
    this.initializingMessage = false
    this.isInitialising = false
  }

  private postInitData() {
    const statsData = {
      type: AppConst.statsMessageType.fullInitData,
      sourceOrigin: window.location.origin,
      content: {
        AllowJigStats: this.myTenant.Subscription.AllowJigStats,
        id_token: this.idToken,
        tenantId: this.myTenant.ID,
      },
      isDebugMode: false,
    }

    if (this.$refs.jigStats.contentWindow) {
      this.$refs.jigStats.contentWindow.postMessage(statsData, this.analyticsUrl)
    }

    window.addEventListener('message', this.handleEventEmitFromIframe)
  }

  private handleEventEmitFromIframe(event: MessageEvent) {
    if (event.origin !== this.analyticsUrl) return
    if (event.source !== this.$refs.jigStats.contentWindow) return
    if (event.data && event.data.type !== AppConst.statsMessageType.eventEmit) return

    switch (event.data.eventName) {
      case AppConst.statsEvents.upgradeClicked:
        eventBus.$emit(AppConst.rootAppEvents.showUpgradeModal)
        break
      case AppConst.statsEvents.bodyViewportUpdated:
        this.setIframeHeight(event.data.height)
        break
      default:
        break
    }
  }
}
